import { useEffect, useRef, useState } from "react";

import { notificationsSocketService } from "@/modules/dashboard/notifications/services/notifications-socket.service";

import { PaymentsPayoutService, PayoutStatus } from "../services/paymentsPayout.service";
import { advancePayoutStatus } from "../utils/payoutStatusProgress";

/** Poll cadence while the payout can still move (same as the payin screens). */
const POLL_INTERVAL_MS = 5_000;
/** 120 × 5 s = 10 min — a rail that has not resolved by then won't do it while the screen is open. */
const POLL_MAX_ATTEMPTS = 120;

/** Payout statuses that end the operation — nothing moves after one of these. */
const TERMINAL_PAYOUT_STATUSES: PayoutStatus[] = [
  PayoutStatus.PAID,
  PayoutStatus.FAILED,
  PayoutStatus.CANCELLED,
  PayoutStatus.REVERSED,
];

export function isTerminalPayoutStatus(status: PayoutStatus | null): boolean {
  return status != null && TERMINAL_PAYOUT_STATUSES.includes(status);
}

/**
 * Live status of a payout while its screen is open, so the dispersion result flips to "paid" (or "failed")
 * the moment the provider resolves it. Mirror of `usePayinLiveStatus`, two channels that coexist (the state
 * update is idempotent):
 *
 * - **Socket** — `payout:updated` on the notifications singleton (rule 10: never open a second socket),
 *   filtered by payout id. Instant, but advisory: a dropped connection loses the event.
 * - **Polling fallback** — `GET /payouts/:id` every 5 s while the status is non-terminal, capped so an
 *   abandoned screen stops asking. Poll errors are swallowed: this hook only ever upgrades the UI.
 *
 * Both channels go through `advancePayoutStatus`, because they answer out of order: a `GET` sent before the
 * webhook can resolve after the socket said PAID, and taking it would put a paid payout back to "in
 * progress". Callers merge the create response the same way (`advancePayoutStatus(response, live)`).
 *
 * Pass `null` to disable (e.g. before the payout exists).
 */
export function usePayoutLiveStatus(payoutId: string | null): PayoutStatus | null {
  const [status, setStatus] = useState<PayoutStatus | null>(null);
  const [seenId, setSeenId] = useState(payoutId);
  const statusRef = useRef(status);
  statusRef.current = status;

  // A new payout id is a new lifecycle — drop the previous one's status. Adjusted during render (the
  // React-documented pattern) so the new payout never renders through the old one's status for a frame.
  if (payoutId !== seenId) {
    setSeenId(payoutId);
    setStatus(null);
    statusRef.current = null;
  }

  // Socket channel.
  useEffect(() => {
    if (!payoutId) return;
    return notificationsSocketService.onPayoutUpdate((payload) => {
      if (payload.id === payoutId) setStatus((current) => advancePayoutStatus(current, payload.status));
    });
  }, [payoutId]);

  // Polling fallback.
  useEffect(() => {
    if (!payoutId) return;
    let attempts = 0;
    // A read still in flight when the payout changes (or the screen closes) belongs to the previous one.
    let active = true;
    const interval = setInterval(() => {
      if (isTerminalPayoutStatus(statusRef.current) || ++attempts > POLL_MAX_ATTEMPTS) {
        clearInterval(interval);
        return;
      }
      PaymentsPayoutService.getPayout(payoutId)
        .then((res) => {
          if (active) setStatus((current) => advancePayoutStatus(current, res.data.status));
        })
        .catch(() => {
          // Transient read failures must not kill the screen — the next tick retries.
        });
    }, POLL_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [payoutId]);

  return status;
}
